import { useCallback, useEffect, useMemo, useState } from 'react'
import { RankmiTable } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip } from 'antd'
import EmptyState from 'components/reusables/emptyState'
import Profiles from 'components/reusables/profiles'
import { dc3AttestationsService } from 'api/administration/dc3Attestations'
import { IDc3AssociatedCourse } from 'interfaces/dc3Attestation'
import { IProfile } from 'interfaces/profiles'
import AssociateNormativeCoursesModal from '../associateNormativeCoursesModal'
import {
  Card,
  Description,
  Footer,
  Header,
  Options,
  RankmiTableWrapper,
  TableSection,
} from './styled'

interface IProps {
  attestationId: number
  onBackToForm: () => void
  onSkip: () => void
  onSaveAssociation: (courseIds: number[]) => Promise<void>
}

const AssociateCoursesView = ({
  attestationId,
  onBackToForm,
  onSkip,
  onSaveAssociation,
}: IProps) => {
  const { t } = useTranslation()
  const prefix = 'administration.certificate.legalAttestationAssociateCourses'
  const [courses, setCourses] = useState<IDc3AssociatedCourse[]>([])
  const [savedCourseIds, setSavedCourseIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchCriteria, setSearchCriteria] = useState('')

  const loadCourses = useCallback(async () => {
    setLoading(true)
    try {
      const response = await dc3AttestationsService.getAssociatedCourses(
        attestationId
      )
      setCourses(response)
      setSavedCourseIds(response.map((course) => course.id))
    } finally {
      setLoading(false)
    }
  }, [attestationId])

  useEffect(() => {
    loadCourses()
  }, [loadCourses])

  const handleOpenAssociateModal = useCallback(() => {
    setModalOpen(true)
  }, [])

  const handleAssociateCourses = (selectedCourses: IDc3AssociatedCourse[]) => {
    const existingIds = new Set(courses.map((course) => course.id))
    const merged = [
      ...courses,
      ...selectedCourses.filter((course) => !existingIds.has(course.id)),
    ]
    setCourses(merged)
  }

  const handleRemoveCourse = (courseId: number) => {
    setCourses((current) => current.filter((course) => course.id !== courseId))
  }

  const handleSaveAssociation = async () => {
    setSaving(true)
    try {
      await onSaveAssociation(courses.map((course) => course.id))
    } finally {
      setSaving(false)
    }
  }

  const hasAssociationChanges = useMemo(() => {
    const currentIds = courses.map((course) => course.id).sort((a, b) => a - b)
    const initialIds = [...savedCourseIds].sort((a, b) => a - b)

    if (currentIds.length !== initialIds.length) return true

    return currentIds.some((id, index) => id !== initialIds[index])
  }, [courses, savedCourseIds])

  const filteredCourses = useMemo(() => {
    const criteria = searchCriteria.trim().toLowerCase()
    if (!criteria) return courses

    return courses.filter((course) =>
      course.name.toLowerCase().includes(criteria)
    )
  }, [courses, searchCriteria])

  const handleSearch = (value: string) => {
    setSearchCriteria(value)
  }

  const locale = useMemo(
    () => ({
      header: {
        addButton: t(`${prefix}.addCourse`),
        rowsCount: t(`${prefix}.modal.rowsCount`),
        rowsCount_plural: t(`${prefix}.modal.rowsCount_plural`),
        search: t('administration.courses.list.search'),
      },
    }),
    [t, prefix]
  )

  const emptyState = useMemo(
    () => (
      <EmptyState
        title={t(`${prefix}.emptyState.title`)}
        description={t(`${prefix}.emptyState.description`)}
        icon={faBook}
        action={handleOpenAssociateModal}
        actionIcon={faPlus}
        actionTitle={t(`${prefix}.emptyState.action`)}
        actionBackground={false}
      />
    ),
    [t, prefix, handleOpenAssociateModal]
  )

  const tableEmpty = useMemo(() => {
    if (courses.length === 0) return emptyState

    return <EmptyState title={t(`${prefix}.modal.searchEmpty`)} icon={faBook} />
  }, [courses.length, emptyState, t, prefix])

  const columns = useMemo(
    () => [
      {
        title: t(`${prefix}.table.course`),
        dataIndex: 'name',
        key: 'name',
        render: (name: string) => <div>{name}</div>,
      },
      {
        title: t(`${prefix}.table.profiles`),
        dataIndex: 'lmsProfiles',
        key: 'lmsProfiles',
        render: (lmsProfiles: IProfile[]) => <Profiles items={lmsProfiles} />,
      },
      {
        width: 66,
        align: 'center' as const,
        fixed: 'right' as const,
        render: (course: IDc3AssociatedCourse) => (
          <Options>
            <Tooltip title={t(`${prefix}.table.remove`)}>
              <Button type="link" onClick={() => handleRemoveCourse(course.id)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </Tooltip>
          </Options>
        ),
      },
    ],
    [t, prefix]
  )

  return (
    <Card id="dc3-attestation-associate-courses">
      <Header>
        <button
          type="button"
          onClick={onBackToForm}
          aria-label={t(`${prefix}.actions.back`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{t(`${prefix}.title`)}</h2>
      </Header>

      <Description>{t(`${prefix}.description`)}</Description>

      <TableSection>
        <RankmiTableWrapper>
          <RankmiTable
            columnHeaders={columns}
            data={filteredCourses.map((course) => ({
              ...course,
              key: course.id,
            }))}
            addButtonIcon={faPlus}
            onAddClick={handleOpenAssociateModal}
            countIcon={faBook}
            locale={locale}
            search={searchCriteria}
            onSearch={handleSearch}
            delaySearch
            loading={loading}
            customEmpty={tableEmpty}
            showFooter={false}
          />
        </RankmiTableWrapper>
      </TableSection>

      <Footer>
        <button type="button" className="back" onClick={onBackToForm}>
          {t(`${prefix}.actions.back`)}
        </button>
        <div className="actions">
          <button type="button" className="secondary" onClick={onSkip}>
            {t(`${prefix}.actions.skip`)}
          </button>
          <button
            type="button"
            className="primary"
            disabled={!hasAssociationChanges || saving}
            onClick={handleSaveAssociation}
          >
            {t(`${prefix}.actions.saveAssociation`)}
          </button>
        </div>
      </Footer>

      {modalOpen && (
        <AssociateNormativeCoursesModal
          excludedCourseIds={courses.map((course) => course.id)}
          onClose={() => setModalOpen(false)}
          onAssociate={handleAssociateCourses}
        />
      )}
    </Card>
  )
}

export default AssociateCoursesView
